import React from 'react';
import { useIsMobile } from '../hooks/useIsMobile';
import { Badge, Btn, SectionHeading, FAQ, AsanohaBackground } from '../components';
import { palette as T, alpha, type } from '../design';
import { usePayApplies } from '../hooks/usePayApplies';
import { PAY_THROUGH_ROPIDA_PRICING_ROW, PAY_THROUGH_ROPIDA_STEP } from '../lib/payments';

// ropida/courses.jsx — Pricing & How It Works (Terracotta Studio)
// (Cohort courses were retired with the session scheduler — instructors now publish
// individually priced sessions, booked from their profiles.)


function PricingPage({ setPage }) {
  const isMobile = useIsMobile();
  // JTP-31: a student Pay applies to reads the Pay row; everyone else reads today's row.
  const paysThroughRopida = usePayApplies();
  const columns = [
    {
      title: '1-on-1 Sessions',
      subtitle: 'Private lessons, your pace.',
      color: T.ivory,
      items: [
        { label: 'Single 1-on-1 (60 min)', price: 'From ₱1,000', note: 'Book any scheduled session' },
        { label: 'Longer sessions (90–120 min)', price: 'Set per session', note: 'Each session lists its own price' },
      ],
      cta: 'Browse instructors →',
      ctaAction: () => setPage('marketplace'),
      featured: false,
    },
    {
      title: 'Group Sessions',
      subtitle: 'Learn with a small group, pay less.',
      color: T.terracotta,
      items: [
        { label: 'Group class (60–90 min)', price: 'From ₱500', note: 'Priced per student, set by the instructor' },
        { label: 'Limited seats', price: '—', note: 'Each session shows seats left — book early' },
        paysThroughRopida
          ? PAY_THROUGH_ROPIDA_PRICING_ROW
          : { label: 'Direct payment', price: 'GCash / bank', note: 'Pay your instructor directly; they confirm your seat' },
      ],
      cta: 'See upcoming sessions →',
      ctaAction: () => setPage('marketplace'),
      featured: true,
    },
    {
      title: 'Enterprise',
      subtitle: 'Teaching a team or group?',
      color: T.ivory,
      items: [
        { label: 'Custom group size', price: 'Custom', note: '3–30 participants' },
        { label: 'Tailored curriculum', price: '—', note: 'Industry-specific vocabulary' },
        { label: 'Flexible scheduling', price: '—', note: 'Fits your team calendar' },
        { label: 'Progress reporting', price: '—', note: 'Per-learner dashboards' },
      ],
      cta: 'Contact us →',
      ctaAction: () => setPage('contact'),
      featured: false,
    },
  ];

  return (
    <AsanohaBackground style={{ paddingTop: 80, minHeight: '100vh' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: isMobile ? '24px 16px' : '60px 32px' }}>
        <div style={{ textAlign: 'center', marginBottom: 56 }}>
          <SectionHeading eyebrow="Simple pricing" title="Pay only for what you need." subtitle="No subscriptions, no surprise fees. Every session lists its own price — set by the instructor." center />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3,1fr)', gap: isMobile ? 16 : 20 }}>
          {columns.map(col => (
            <div key={col.title} style={{ borderRadius: 24, overflow: 'hidden', border: col.featured ? '2px solid ' + T.terracotta : '1px solid ' + T.border, boxShadow: col.featured ? '0 12px 48px ' + alpha('terracotta', 'shadow') : '0 4px 20px ' + alpha('espresso', 'shadow'), position: 'relative' }}>
              {col.featured && (
                <div style={{ position: 'absolute', top: 16, right: 16 }}><Badge variant="terracotta">Most popular</Badge></div>
              )}
              <div style={{ background: col.color, padding: '28px 28px 24px' }}>
                <h3 style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: 24, color: col.featured ? T.white : T.espresso, margin: '0 0 6px' }}>{col.title}</h3>
                <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: col.featured ? T.white : T.walnut, margin: 0 }}>{col.subtitle}</p>
              </div>
              <div style={{ background: T.white, padding: '24px 28px' }}>
                {col.items.map((item, i) => (
                  <div key={i} style={{ paddingBottom: 14, marginBottom: 14, borderBottom: i < col.items.length - 1 ? '1px solid ' + T.border : 'none' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                      <span style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.espresso }}>{item.label}</span>
                      <span style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: type.body, color: T.terracotta, whiteSpace: 'nowrap' }}>{item.price}</span>
                    </div>
                    <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.caption, color: T.driftwood, margin: '2px 0 0' }}>{item.note}</p>
                  </div>
                ))}
                <Btn onClick={col.ctaAction} variant={col.featured ? 'primary' : 'ghost'} style={{ width: '100%', justifyContent: 'center', marginTop: 8 }}>{col.cta}</Btn>
              </div>
            </div>
          ))}
        </div>

        {/* Currency note */}
        <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.driftwood, textAlign: 'center', marginTop: 32 }}>
          All prices in Philippine Pesos (₱ PHP). Payment is arranged directly with your instructor (GCash or bank transfer) — they confirm your booking once payment is received.
        </p>

        {/* FAQ */}
        <div style={{ marginTop: 64 }}>
          <SectionHeading eyebrow="Pricing FAQ" title="Common questions" center />
          <div style={{ marginTop: 32 }}>
            <FAQ items={[
              { q: 'How do I pay?', a: 'After you request a booking, you\'ll see the instructor\'s payment instructions (GCash or bank details). Send your payment directly, then open My Bookings and tap "I\'ve paid" to attach a screenshot of your payment to the booking. Your instructor sees it there and confirms your seat once the payment arrives.' },
              { q: 'Do prices differ per instructor?', a: 'Yes, each instructor prices every session they schedule. The prices above reflect starting rates — check the sessions on each profile for exact pricing.' },
            ]} />
          </div>
        </div>
      </div>
    </AsanohaBackground>
  );
}

// ── How It Works ──────────────────────────────────────────────────────────────

function HowItWorksPage({ setPage }) {
  const isMobile = useIsMobile();
  // JTP-31: a student Pay applies to reads the Pay step 3; everyone else reads today's step.
  const paysThroughRopida = usePayApplies();
  const steps = [
    { num: 1, title: 'Browse instructors', desc: 'Filter by language (Japanese or Mandarin), level, specialty, and budget. Read bios, watch intro videos, check reviews. Take your time — every teacher here is vetted.' },
    { num: 2, title: 'Book a session', desc: 'Each instructor publishes upcoming sessions — 1-on-1 or small group — with the date, duration, seats, and price. Times are shown in your own timezone. Pick one that fits and request a seat.' },
    paysThroughRopida
      ? { num: 3, ...PAY_THROUGH_ROPIDA_STEP }
      : { num: 3, title: 'Pay your instructor directly', desc: 'You\'ll get the instructor\'s payment details (GCash or bank transfer) right after booking. Send your payment, then attach your payment screenshot to the booking: open My Bookings and tap "I\'ve paid". Your instructor confirms your seat once the payment arrives — you\'ll get an email when it\'s confirmed.' },
    { num: 4, title: 'Meet online', desc: 'Your class happens via the meeting link in your dashboard once your booking is confirmed. Bring your coffee, bring your notebook — let\'s talk about it.' },
    { num: 5, title: 'Keep going', desc: 'Love your teacher? Book their next sessions from their profile. Your student dashboard keeps your schedule, history, and materials in one place.' },
  ];

  return (
    <AsanohaBackground style={{ paddingTop: 80, minHeight: '100vh' }}>
      <div style={{ maxWidth: 840, margin: '0 auto', padding: isMobile ? '40px 16px' : '60px 32px' }}>
        <div style={{ textAlign: 'center', marginBottom: isMobile ? 40 : 64 }}>
          <SectionHeading eyebrow="The process" title="How Ropida works" subtitle="From nervous beginner to confident learner — here's the path." center />
        </div>

        <div style={{ position: 'relative' }}>
          {/* Vertical line */}
          <div style={{ position: 'absolute', left: isMobile ? 26 : 44, top: 0, bottom: 0, width: 2, background: 'linear-gradient(' + T.border + ', ' + T.terracotta + ')', borderRadius: 2 }} />

          {steps.map((step, i) => (
            <div key={step.num} style={{ display: 'flex', gap: isMobile ? 16 : 32, marginBottom: i < steps.length - 1 ? (isMobile ? 28 : 48) : 0, alignItems: 'flex-start' }}>
              <div style={{ width: isMobile ? 52 : 90, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 52, height: 52, borderRadius: '50%', background: T.ivory, border: '3px solid ' + T.white, boxShadow: '0 4px 16px ' + alpha('espresso', 'shadow'), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: type.lead, fontWeight: 700, fontFamily: "'Plus Jakarta Sans', sans-serif", color: T.terracotta, position: 'relative', zIndex: 1 }}>
                  {step.num}
                </div>
                {/* Desktop "Step N" caption removed: the numbered circle + connector line
                    already convey the sequence, and the caption sat on top of the line (TTT-16). */}
              </div>
              <div style={{ flex: 1, background: T.white, borderRadius: 20, border: '1px solid ' + T.border, padding: isMobile ? 16 : 28 }}>
                {isMobile && <span style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: type.caption, color: T.terracotta, textTransform: 'uppercase', letterSpacing: 1, display: 'block', marginBottom: 4 }}>Step {step.num}</span>}
                <h3 style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: isMobile ? type.h4 : 22, color: T.espresso, margin: '0 0 8px' }}>{step.title}</h3>
                <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.walnut, lineHeight: 1.75, margin: 0 }}>{step.desc}</p>
              </div>
            </div>
          ))}
        </div>

        <div style={{ textAlign: 'center', marginTop: 64 }}>
          <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.h4, color: T.terracotta, marginBottom: 16, fontStyle: 'italic' }}>Ready? Let's begin.</p>
          <Btn size="lg" onClick={() => setPage('marketplace')}>Find your sensei →</Btn>
        </div>
      </div>
    </AsanohaBackground>
  );
}

export { PricingPage, HowItWorksPage };
